// Component: MailAIModal
// Purpose: Modal to generate mail template metadata (search terms, tags)
// from a freeform description using server-side AI and return it to parent.
// Props:
// - isOpen: boolean controls visibility
// - onClose: () => void close modal
// - onGenerateSuccess: (data) => void return generated fields
import React, { useState, useEffect } from 'react';
import { X, Sparkles, Mail } from 'lucide-react';
import { Label } from '../../ui/inputs/label';
import { ButtonSpinner } from '../../ui/feedback/loading-spinner.jsx';
import { SERVER_URL } from '../../../api/core';

export function MailAIModal({ isOpen, onClose, onGenerateSuccess }) {
  const [content, setContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [localError, setLocalError] = useState('');
  const [generatedData, setGeneratedData] = useState(null);
  
  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (localError) {
      const timer = setTimeout(() => {
        setLocalError('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [localError]);
  
  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setContent('');
      setGeneratedData(null);
      setLocalError('');
    }
  }, [isOpen]);
  
  // Handle AI generation
  const handleAIGenerate = async () => {
    if (!content.trim()) {
      setLocalError('Please describe what the mail template is about');
      return;
    }
    
    setIsGenerating(true);
    setLocalError('');
    
    try {
      const response = await fetch(`${SERVER_URL}/templatesAi/generate-mail-search-terms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: content.trim(),
        }),
      });
      
      const data = await response.json();
      
      if (data.status === 'error') {
        throw new Error(data.message || 'Failed to generate mail template data');
      }
      
      // Success - store the data and show preview
      setGeneratedData(data);
      
    } catch (error) {
      console.error('AI generation error:', error);
      setLocalError(error.message || 'Failed to generate mail template data');
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle using the generated data
  const handleUseGenerated = () => {
    if (generatedData && (generatedData.searchTerms || generatedData.tags)) {
      onGenerateSuccess({
        searchTerms: generatedData.searchTerms || '',
        tags: generatedData.tags || []
      });
      onClose();
    }
  };
  
  // Check if content has content to enable the button
  const canSubmit = content && content.trim();
  const hasGeneratedData = generatedData && (generatedData.searchTerms || generatedData.tags);
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900 mx-4">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/50">
              <Mail className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                AI Mail Template Generator
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Generate search terms and tags for your mail template
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="mail-content">What is this mail template about?</Label>
            <textarea
              id="mail-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="e.g., A fundraising postcard for a Republican Senate candidate supporting property tax relief for Texas homeowners"
              rows={4}
              className="mt-2 block w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 placeholder:text-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/10 dark:border-gray-800 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-purple-400 dark:focus:ring-purple-400/10 resize-none"
            />
          </div>
          
          {localError && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/50 dark:text-red-400">
              <X className="h-4 w-4" />
              {localError}
            </div>
          )}
          
          <div className="flex items-center gap-3 rounded-lg border border-purple-100 bg-purple-50/50 p-3 dark:border-purple-900/50 dark:bg-purple-900/10">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/50">
              <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <p className="text-sm text-purple-700 dark:text-purple-400">
              AI will analyze your description and generate relevant search terms (10-15) and appropriate tags from the mail template categories.
            </p>
          </div>
          
          {hasGeneratedData && (
            <div className="space-y-3">
              {generatedData.searchTerms && (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Generated Search Terms</label>
                  <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border">
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {generatedData.searchTerms}
                    </p>
                  </div>
                </div>
              )}
              
              {generatedData.tags && generatedData.tags.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Generated Tags</label>
                  <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border">
                    <div className="flex flex-wrap gap-1">
                      {generatedData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded dark:bg-purple-900/30 dark:text-purple-300"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-500">
                This data will be used to populate your mail template form.
              </p>
            </div>
          )}
          
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            
            {!hasGeneratedData ? (
              <button
                type="button"
                onClick={handleAIGenerate}
                disabled={!canSubmit || isGenerating}
                className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-purple-500 disabled:bg-purple-500 disabled:opacity-50"
              >
                {isGenerating ? (
                  <>
                    <ButtonSpinner color="white" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate Template Data
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleUseGenerated}
                className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-purple-500"
              >
                <Mail className="h-4 w-4" />
                Use This Data
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
